import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { FileSpreadsheet, ChevronLeft, ChevronRight, Search, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;

const PLATFORMS = [
  { key: "emag_ro_fbe", label: "🇷🇴 eMAG RO FBE" },
  { key: "emag_ro_bau", label: "🇷🇴 eMAG RO BAU" },
  { key: "emag_bg_fbe", label: "🇧🇬 eMAG BG FBE" },
  { key: "emag_bg_bau", label: "🇧🇬 eMAG BG BAU" },
  { key: "emag_hu_fbe", label: "🇭🇺 eMAG HU FBE" },
  { key: "emag_hu_bau", label: "🇭🇺 eMAG HU BAU" },
  { key: "gomag_bragus", label: "🇷🇴 Gomag BRAGUS" },
];
const STATUSES = [
  { v: "1", label: "Nouă" }, { v: "2", label: "În procesare" },
  { v: "3", label: "Pregătită" }, { v: "4", label: "Finalizată" },
  { v: "0", label: "Anulată" }, { v: "5", label: "Returnată" },
];
const DELIVERY_MODES = [
  { v: "home", label: "Domiciliu" }, { v: "locker", label: "Locker / Easybox" },
];
const PAYMENT_MODES = [
  { v: "online", label: "Online (card)" }, { v: "cod", label: "Ramburs (COD)" },
];

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const fmt = (n) => new Intl.NumberFormat("ro-RO").format(n);
const fmtMoney = (n, currency = "RON") => new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 2 }).format(n) + " " + currency;
// Backend converteste EUR (BG) și HUF (HU) în RON folosind cursul BNR, așa
// că afișăm mereu în RON. Câmpul `data.currency` din răspuns confirmă.
const deriveCurrency = () => "RON";

// ============ Shared filter UI ============
function FilterPills({ label, options, selected, onToggle, testidPrefix }) {
  return (
    <div>
      <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = selected.includes(o.v ?? o.key);
          const v = o.v ?? o.key;
          return (
            <button key={v} data-testid={`${testidPrefix}-${v}`} onClick={() => onToggle(v)}
              className={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition ${active ? "border-orange-500/50 bg-orange-500/15 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRange({ from, to, onChange }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">De la</label>
        <input data-testid="report-date-from" type="date" value={from} onChange={(e) => onChange({ from: e.target.value, to })}
          className="mt-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-100" />
      </div>
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Până la</label>
        <input data-testid="report-date-to" type="date" value={to} onChange={(e) => onChange({ from, to: e.target.value })}
          className="mt-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-100" />
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent = "text-orange-300" }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5 text-center">
      <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`mt-2 text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

// ============ Hook: shared filter state ============
function useReportFilters() {
  const [filters, setFilters] = useState({
    date_from: firstOfMonth(), date_to: today(),
    statuses: ["1", "2", "4"], platforms: [], delivery_modes: [], payment_modes: [],
  });
  const toggle = (key) => (val) => setFilters((f) => {
    const set = new Set(f[key] || []);
    set.has(val) ? set.delete(val) : set.add(val);
    return { ...f, [key]: [...set] };
  });
  const setDates = ({ from, to }) => setFilters((f) => ({ ...f, date_from: from, date_to: to }));
  const queryParams = useMemo(() => {
    const p = { date_from: filters.date_from, date_to: filters.date_to };
    if (filters.statuses.length) p.statuses = filters.statuses.join(",");
    if (filters.platforms.length) p.platforms = filters.platforms.join(",");
    if (filters.delivery_modes.length) p.delivery_modes = filters.delivery_modes.join(",");
    if (filters.payment_modes.length) p.payment_modes = filters.payment_modes.join(",");
    return p;
  }, [filters]);
  return { filters, toggle, setDates, queryParams };
}

function SharedFilters({ filters, toggle }) {
  return (
    <>
      <FilterPills label="Status comandă" options={STATUSES} selected={filters.statuses} onToggle={toggle("statuses")} testidPrefix="filter-status" />
      <FilterPills label="Platformă" options={PLATFORMS} selected={filters.platforms} onToggle={toggle("platforms")} testidPrefix="filter-platform" />
      <FilterPills label="Metodă livrare" options={DELIVERY_MODES} selected={filters.delivery_modes} onToggle={toggle("delivery_modes")} testidPrefix="filter-delivery" />
      <FilterPills label="Metodă plată" options={PAYMENT_MODES} selected={filters.payment_modes} onToggle={toggle("payment_modes")} testidPrefix="filter-payment" />
    </>
  );
}

// ============ SALES TAB ============
function SalesReport() {
  const { filters, toggle, setDates, queryParams } = useReportFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const currency = deriveCurrency(filters.platforms);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/reports/sales`, { params: queryParams });
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [queryParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-5">
      <DateRange from={filters.date_from} to={filters.date_to} onChange={setDates} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SharedFilters filters={filters} toggle={toggle} />
      </div>

      {err && <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300">{err}</div>}

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
        <p className="mb-3 text-sm font-semibold text-sky-300">Numărul de comenzi</p>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data?.daily || []}>
            <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
            </linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="day" stroke="#71717a" fontSize={10} tickFormatter={(v) => v.slice(5)} />
            <YAxis stroke="#71717a" fontSize={10} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, fontSize: 11 }} />
            <Area type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2.5} fill="url(#g1)" name="Comenzi" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Valoarea totală" value={loading ? "..." : fmtMoney(data?.totals?.revenue || 0, currency)} />
        <KpiCard label="Numărul total de comenzi" value={loading ? "..." : fmt(data?.totals?.orders || 0)} />
        <KpiCard label="Media" value={loading ? "..." : fmtMoney(data?.totals?.avg || 0, currency)} />
        <KpiCard label="Total clienți" value={loading ? "..." : fmt(data?.totals?.customers || 0)} />
      </div>
    </div>
  );
}

// ============ PROFIT TAB ============
function ProfitReport() {
  const { filters, toggle, setDates, queryParams } = useReportFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/reports/profit`, { params: queryParams });
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [queryParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data?.totals || {};
  return (
    <div className="space-y-5">
      <DateRange from={filters.date_from} to={filters.date_to} onChange={setDates} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SharedFilters filters={filters} toggle={toggle} />
      </div>

      {err && <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300">{err}</div>}

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
        <p className="mb-3 text-sm font-semibold text-emerald-300">Profit zilnic vs încasări</p>
        <p className="mb-3 text-[10px] text-zinc-500">Profit NET cu costuri, comisioane eMAG, transport, ads, vouchere · BG/HU convertite în RON cu cursul BNR</p>
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart data={data?.daily || []}>
            <defs>
              <linearGradient id="gProfit" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="day" stroke="#71717a" fontSize={10} tickFormatter={(v) => v.slice(5)} />
            <YAxis stroke="#71717a" fontSize={10} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip
              contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, fontSize: 11 }}
              formatter={(v, name) => [fmtMoney(v), name]}
            />
            <Area type="monotone" dataKey="revenue" name="Încasări" stroke="#0ea5e9" strokeWidth={2} fill="url(#gRev)" />
            <Area type="monotone" dataKey="profit" name="Profit" stroke="#10b981" strokeWidth={2.5} fill="url(#gProfit)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Profit total" value={loading ? "..." : fmtMoney(totals.profit || 0)} accent="text-emerald-300" />
        <KpiCard label="Încasări totale" value={loading ? "..." : fmtMoney(totals.revenue || 0)} accent="text-sky-300" />
        <KpiCard label="Marjă medie" value={loading ? "..." : `${totals.margin_pct ?? 0}%`} accent={totals.margin_pct >= 20 ? "text-emerald-300" : totals.margin_pct >= 10 ? "text-amber-300" : "text-rose-300"} />
        <KpiCard label="Comenzi" value={loading ? "..." : fmt(totals.orders || 0)} />
      </div>

      {(totals.best_day || totals.worst_day) && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {totals.best_day && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300">Cea mai bună zi</p>
              <p className="mt-1 text-lg font-semibold text-emerald-200">{totals.best_day.day}</p>
              <p className="mt-0.5 text-xs text-zinc-400">Profit: <span className="text-emerald-300">{fmtMoney(totals.best_day.profit)}</span> · {totals.best_day.orders} comenzi</p>
            </div>
          )}
          {totals.worst_day && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
              <p className="text-[10px] uppercase tracking-wider text-rose-300">Cea mai slabă zi</p>
              <p className="mt-1 text-lg font-semibold text-rose-200">{totals.worst_day.day}</p>
              <p className="mt-0.5 text-xs text-zinc-400">Profit: <span className="text-rose-300">{fmtMoney(totals.worst_day.profit)}</span> · {totals.worst_day.orders} comenzi</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ PRODUCTS TAB ============
function ProductsReport() {
  const { filters, toggle, setDates, queryParams } = useReportFilters();
  const currency = deriveCurrency(filters.platforms);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState("");

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearchDebounced(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [queryParams]);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = { ...queryParams, page, page_size: PAGE_SIZE };
      if (searchDebounced.trim()) params.search = searchDebounced.trim();
      const r = await axios.get(`${API}/reports/products`, { params });
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [queryParams, page, searchDebounced]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...queryParams };
      if (searchDebounced.trim()) params.search = searchDebounced.trim();
      const r = await axios.get(`${API}/reports/products/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `raport-produse-${filters.date_from}_${filters.date_to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setExporting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <DateRange from={filters.date_from} to={filters.date_to} onChange={setDates} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SharedFilters filters={filters} toggle={toggle} />
      </div>

      {/* Search bar */}
      <div>
        <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Caută</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input data-testid="products-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după nume produs sau SKU (ex: tastatura, BRG-1234)..."
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-10 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
        </div>
      </div>

      {err && <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300">{err}</div>}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500" data-testid="products-count">
          <span className="font-semibold text-zinc-300">{fmt(data.total)}</span> produse
          {data.total > PAGE_SIZE && <> — pagina {page} din {totalPages}</>}
        </div>
        <button data-testid="products-export-btn" onClick={handleExport} disabled={exporting || !data.total}
          className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-sky-500/20 transition hover:bg-sky-400 disabled:cursor-not-allowed disabled:bg-zinc-700">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
          {exporting ? "Se generează..." : "Exportă"}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/70">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-800 bg-zinc-900/50">
            <tr className="text-left text-[10px] font-medium uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">Numele produselor</th>
              <th className="px-4 py-3 text-right w-32">Cantitatea</th>
              <th className="px-4 py-3 text-right w-44">Valoarea produselor</th>
            </tr>
          </thead>
          <tbody data-testid="products-tbody">
            {loading && (
              <tr><td colSpan={3} className="p-8 text-center text-sm text-zinc-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            )}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={3} className="p-12 text-center text-sm text-zinc-500">Niciun produs găsit pentru filtrele selectate.</td></tr>
            )}
            {!loading && data.rows.map((p) => (
              <tr key={p.sku} className="border-t border-zinc-800/60 transition hover:bg-zinc-900/40">
                <td className="px-4 py-3">
                  <div className="text-sky-300">{p.name || "—"}</div>
                  <div className="mt-0.5 text-[11px] font-mono text-zinc-500">{p.sku}</div>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-200">{fmt(p.quantity)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-zinc-100">{fmtMoney(p.value, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button data-testid="products-page-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700 disabled:opacity-40">
            <ChevronLeft className="h-3.5 w-3.5" /> Anterior
          </button>
          <span className="text-xs text-zinc-500">Pagina {page} din {totalPages}</span>
          <button data-testid="products-page-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700 disabled:opacity-40">
            Următor <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============ MAIN ============
export default function Reports() {
  const [activeTab, setActiveTab] = useState("sales");
  const tabLabel = activeTab === "sales" ? "Vânzări" : activeTab === "profit" ? "Profit" : "Produse";
  return (
    <div className="space-y-5 p-6">
      <div>
        <div className="text-[11px] text-zinc-500">Panou de control / Rapoarte / <span className="text-zinc-300">{tabLabel}</span></div>
        <h1 className="mt-1 text-2xl font-bold text-zinc-50">Rapoarte</h1>
      </div>
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button data-testid="reports-tab-sales" onClick={() => setActiveTab("sales")}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${activeTab === "sales" ? "border-orange-500 text-orange-200" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
          Vânzări
        </button>
        <button data-testid="reports-tab-profit" onClick={() => setActiveTab("profit")}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${activeTab === "profit" ? "border-orange-500 text-orange-200" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
          Profit
        </button>
        <button data-testid="reports-tab-products" onClick={() => setActiveTab("products")}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${activeTab === "products" ? "border-orange-500 text-orange-200" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
          Produse
        </button>
      </div>
      {activeTab === "sales" && <SalesReport />}
      {activeTab === "profit" && <ProfitReport />}
      {activeTab === "products" && <ProductsReport />}
    </div>
  );
}
